// components/QuotationPDFTemplate.jsx
import React, { forwardRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider
} from '@mui/material';

import { useRestaurant } from '../contexts/RestaurantContext';


const QuotationPDFTemplate = forwardRef(({ data }, ref) => {


  const { restaurantData } = useRestaurant()

  if (!data) return null;
  

  // console.log("Restaurant Logo ", restaurantData.logo_url)


  // Calculate amounts
  const calculateAmounts = () => {
    const foodCost = parseFloat(data.food_cost) || 0;
    const buffetCost = parseFloat(data.buffet_cost) || 0;
    const transportCost = parseFloat(data.transport_cost) || 0;
    const discountPercent = parseFloat(data.discount) || 0;
    const gstPercent = parseFloat(data.gst) || 0;

    const subtotal = foodCost + buffetCost + transportCost;
    const discountAmount = (subtotal * discountPercent) / 100;
    const afterDiscount = subtotal - discountAmount;
    const gstAmount = (afterDiscount * gstPercent) / 100;
    const total = afterDiscount + gstAmount;

    return {
      foodCost,
      buffetCost,
      transportCost,
      discountPercent,
      discountAmount,
      gstPercent,
      gstAmount,
      total: parseFloat(data.total_amount) || total
    };
  };

  const amounts = calculateAmounts();

  return (
    <Box
      ref={ref}
      sx={{
        p: 4,
        bgcolor: 'white',
        width: '210mm',
        minHeight: '297mm',
        mx: 'auto',
        // REMOVED visibility: 'hidden' - this was hiding the content!
        // REMOVED position: 'absolute' and left: '-9999px' - these should be in the provider
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Box sx={{
            width: 150,
            height: 60,
            bgcolor: restaurantData?.logo_url ? 'transparent' : '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: restaurantData?.logo_url ? 'none' : '1px dashed #ccc',
            mb: 1,
            overflow: 'hidden'
          }}>
            {restaurantData?.logo_url ? (
              // If logo is a URL or base64 string
              <img
                src={restaurantData?.logo_url}
                alt={"Smart F & B"}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  // Fallback if image fails to load
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<span>Logo</span>';
                }}
              />
            ) : (
              <Typography color="textSecondary">Logo</Typography>
            )}
          </Box>

          <Typography variant="body2" sx={{ mt: 1 }}>
            123 Catering Lane, Business Hub<br />
            Phone: {restaurantData?.phone}<br />
            Email: orders@companycatering.com
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h4" sx={{ color: '#680000', fontWeight: 'bold', mb: 1 }}>
            CATERING QUOTATION
          </Typography>
          <Typography variant="body2">
            <strong>Quotation No:</strong> {data.quotation_number}<br />
            <strong>Date:</strong> {new Date().toLocaleDateString('en-CA')}<br />
            <strong>Type:</strong> {data.quotation_type}
          </Typography>
        </Box>
      </Box>

      {/* Customer and Event Info */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={6}>
          <Typography variant="subtitle1" sx={{
            color: '#680000',
            fontWeight: 'bold',
            borderBottom: '2px solid #680000',
            pb: 0.5,
            mb: 1
          }}>
            CUSTOMER DETAILS
          </Typography>
          <Typography variant="body1"><strong>{data.customer_name}</strong></Typography>
          <Typography variant="body2">Phone: {data.customer_phone}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1" sx={{
            color: '#680000',
            fontWeight: 'bold',
            borderBottom: '2px solid #680000',
            pb: 0.5,
            mb: 1
          }}>
            EVENT LOGISTICS
          </Typography>
          <Typography variant="body2"><strong>Venue:</strong> {data.venue_address}</Typography>
          <Typography variant="body2"><strong>Event Date:</strong> {data.event_date}</Typography>
          <Typography variant="body2"><strong>Event Time:</strong> {data.event_time}</Typography>
          <Typography variant="body2"><strong>Delivery Type:</strong> {data.delivery_type}</Typography>
        </Grid>
      </Grid>

      {/* Menu Items Table */}
      <Typography variant="subtitle1" sx={{
        color: '#680000',
        fontWeight: 'bold',
        borderBottom: '2px solid #680000',
        pb: 0.5,
        mb: 2
      }}>
        MENU ITEMS
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#680000' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>S.No</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Menu Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Menu Items</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Qty</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Price (₹)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.menu_details?.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Typography fontWeight="bold" sx={{ color: '#680000' }}>
                    {item.menu_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  {item.menu_items?.map((subItem, i) => (
                    <Typography key={i} variant="body2">• {subItem}</Typography>
                  ))}
                  {item.add_ons?.length > 0 && (
                    <>
                      <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>Add-ons:</Typography>
                      {item.add_ons.map((addon, i) => (
                        <Typography key={`addon-${i}`} variant="body2">• {addon}</Typography>
                      ))}
                    </>
                  )}
                </TableCell>
                <TableCell align="center">{item.quantity}</TableCell>
                <TableCell align="right">₹ {item.price.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Section */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
        <Box sx={{ width: '50%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography>Food Cost:</Typography>
            <Typography>₹ {amounts.foodCost.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography>Buffet Service Charges:</Typography>
            <Typography>₹ {amounts.buffetCost.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, color: 'text.secondary' }}>
            <Typography fontStyle="italic">Transport Cost:</Typography>
            <Typography>₹ {amounts.transportCost.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, color: '#680000' }}>
            <Typography>Discount ({amounts.discountPercent}%):</Typography>
            <Typography>- ₹ {amounts.discountAmount.toFixed(2)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography>GST ({amounts.gstPercent}%):</Typography>
            <Typography>₹ {amounts.gstAmount.toFixed(2)}</Typography>
          </Box>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            py: 2,
            bgcolor: '#f5f5f5',
            px: 2,
            mt: 1,
            borderTop: '2px solid #680000',
            borderBottom: '2px solid #680000'
          }}>
            <Typography variant="h6" sx={{ color: '#680000', fontWeight: 'bold' }}>
              Total Amount
            </Typography>
            <Typography variant="h6" sx={{ color: '#680000', fontWeight: 'bold' }}>
              ₹ {amounts.total.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid #e0e0e0' }}>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" sx={{ color: '#680000', fontWeight: 'bold', mb: 1 }}>
              TERMS & CONDITIONS
            </Typography>
            <Typography variant="body2" component="div">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>50% advance payment required to confirm the booking.</li>
                <li>Final headcount must be confirmed 48 hours prior to the event.</li>
                <li>Government taxes are applicable as per current regulations.</li>
                <li>Cancellations within 24 hours are subject to a 50% charge.</li>
              </ul>
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="subtitle2" sx={{ color: '#680000', fontWeight: 'bold', mb: 1 }}>
              Contact Us
            </Typography>
            <Typography variant="body2">For any queries regarding this quotation, please contact:</Typography>
            <Typography variant="body2" sx={{ color: '#680000', fontWeight: 'bold', mt: 1 }}>
              Admin Support
            </Typography>
            <Typography variant="body2">support@companycatering.com | +91 90000 00001</Typography>
          </Grid>
        </Grid>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" sx={{ color: '#680000', fontStyle: 'italic' }}>
            Thank you for choosing our services!
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1, display: 'block' }}>
            This is a computer generated quotation.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

QuotationPDFTemplate.displayName = 'QuotationPDFTemplate';

export default QuotationPDFTemplate;